import { useEffect, useMemo, useState } from "react"
import Accordion from "@/v2-ui/accordion"
import type { PublicCategoryStatus } from "@/v2-status-client/status/status.types"
import StatusCategoryAccordionItem from "@/v2-status-client/status/category/StatusCategoryAccordionItem"

export type StatusCategoryAccordionProps = {
  categories: PublicCategoryStatus[]
  // Category id encoded in the URL hash (`#category-foo`). When
  // present, that category's accordion auto-opens so a customer
  // following a deep link from support lands on an expanded row.
  // Empty string means no category is targeted by the hash.
  activeCategoryId: string
}

// Vertical accordion of category rows for the per-platform detail
// view. Two responsibilities:
//
//   1. Render the categories as separate cards so a customer can
//      collapse what they don't care about. The user explicitly
//      asked for parents to act like accordions; defaulting every
//      panel to closed keeps the page short.
//   2. Tie open-state to the URL: a `#category-X` hash opens the X
//      panel, and a `#service-X` hash opens whichever category
//      contains service X (so deep links to services don't land
//      inside a collapsed panel and silently scroll to nothing).
//
// Open state is multi-select (`type="multiple"`) so manual
// expansions stick around when the customer taps a different pill;
// closing one doesn't fold others. The set of currently open ids
// is fully controlled here -- letting Radix manage it would mean
// we couldn't push the active category in from the URL.
function StatusCategoryAccordion(
  props: Readonly<StatusCategoryAccordionProps>,
) {
  const { categories, activeCategoryId } = props
  const initial = useMemo(
    () => initialOpenIds(categories, activeCategoryId),
    // Recompute only when the category set itself changes; the
    // activeCategoryId effect below keeps the open list in sync
    // for in-page nav after the initial computation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [categories],
  )
  const [openIds, setOpenIds] = useState<string[]>(initial)

  // Keep the URL-targeted category in the open set as the customer
  // navigates: clicking a sidebar entry fires a pushState /
  // hashchange that updates `activeCategoryId`, and we mirror that
  // into the accordion so the row visibly expands on the same
  // click. We never *close* a panel from this effect -- any
  // manually-opened panels stay open so the customer's prior
  // exploration isn't yanked out from under them.
  //
  // When the hash points at a sub-category (e.g. a sidebar click
  // on a layer-category leaf), the active id matches one of the
  // *child* category ids; we look up the parent and open *that*
  // so the deep-linked sub-section is reachable in the DOM.
  useEffect(() => {
    if (!activeCategoryId) return
    const parentId = parentIdFor(categories, activeCategoryId)
    if (!parentId) return
    setOpenIds((prev) => (prev.includes(parentId) ? prev : [...prev, parentId]))
  }, [activeCategoryId, categories])

  return (
    <Accordion
      type="multiple"
      value={openIds}
      onValueChange={setOpenIds}
      className="flex flex-col gap-2"
    >
      {categories.map((c) => (
        <StatusCategoryAccordionItem key={c.id} category={c} />
      ))}
    </Accordion>
  )
}

// initialOpenIds picks the categories that should be expanded on
// first render. Three sources:
//
//   - activeCategoryId: the explicit `#category-foo` deep link.
//     When the hash targets a *sub-category*, we resolve it to its
//     parent so the panel containing the sub-section is open.
//   - service hash: a `#service-foo` deep link points at a row
//     inside one category's expanded list; we open that parent so
//     the scroll-to-hash hook can actually find the element.
//
// Anything else stays closed; the customer reaches into a category
// only when they care about its services.
function initialOpenIds(
  categories: PublicCategoryStatus[],
  activeCategoryId: string,
): string[] {
  const out = new Set<string>()
  if (activeCategoryId) {
    const parentId = parentIdFor(categories, activeCategoryId)
    if (parentId) out.add(parentId)
  }
  const serviceId = readActiveServiceId()
  if (serviceId) {
    const parentId = parentIdForService(categories, serviceId)
    if (parentId) out.add(parentId)
  }
  return [...out]
}

// parentIdFor resolves a category id to the id of the accordion
// item that needs to be open in order for the targeted row to be
// reachable in the DOM. Three cases:
//   - The id is a top-level category → return it (it's its own
//     parent in the accordion sense).
//   - The id is a sub-category → return the id of the parent that
//     hosts it.
//   - The id is unknown → return "" so the caller can ignore it.
function parentIdFor(categories: PublicCategoryStatus[], id: string): string {
  for (const c of categories) {
    if (c.id === id) return c.id
    if (c.subCategories?.some((sub) => sub.id === id)) return c.id
  }
  return ""
}

// parentIdForService finds which top-level category contains a
// service id, looking through both the parent's own services and
// any sub-category's services. Returns "" when the service isn't
// in this platform's categories at all (stale link, dead probe).
function parentIdForService(
  categories: PublicCategoryStatus[],
  serviceId: string,
): string {
  for (const c of categories) {
    if (c.services.some((s) => s.id === serviceId)) return c.id
    if (
      c.subCategories?.some((sub) =>
        sub.services.some((s) => s.id === serviceId),
      )
    ) {
      return c.id
    }
  }
  return ""
}

// readActiveServiceId returns the service id encoded in the URL
// hash (`#service-foo` -> `foo`), or empty string for any other
// hash shape. Kept private to the accordion module because nothing
// else on the page cares about service-level deep linking yet --
// the visual nav only exposes category jumps. If a second consumer
// shows up, lift this into status.filter.ts to keep the URL
// vocabulary in one place.
function readActiveServiceId(): string {
  if (typeof window === "undefined") return ""
  const hash = window.location.hash.slice(1)
  const prefix = "service-"
  if (!hash.startsWith(prefix)) return ""
  return hash.slice(prefix.length)
}

export default StatusCategoryAccordion
